import { useFetcher, useRouteLoaderData, useSearchParams } from '@remix-run/react';
import { Button } from 'flowbite-react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface EditableTextProps {
    ns: string;
    translationKey: string;
    t: (key: string, opts?: Record<string, any>) => string;
    opts?: Record<string, any>;
    pluralVariants?: { zero?: string; one?: string; other?: string };
}

interface EditModeContextValue {
    isEditMode: boolean;
    editModePassword: string | null;
}

interface TextPart {
    type: 'text' | 'placeholder';
    content: string;
}

const EditModeContext = createContext<EditModeContextValue>({ isEditMode: false, editModePassword: null });

// Parse text into parts: text and placeholders
function parseTextWithPlaceholders(text: string): TextPart[] {
    const parts: TextPart[] = [];
    const regex = /(\{\{[^}]+\}\}|\$t\([^)]+\))/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        // Add text before placeholder
        if (match.index > lastIndex) {
            parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
        }
        // Add placeholder
        parts.push({ type: 'placeholder', content: match[0] });
        lastIndex = regex.lastIndex;
    }
    // Add remaining text
    if (lastIndex < text.length) {
        parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    return parts;
}

// Interactive text editor with draggable placeholder pills
function InteractiveTextEditor(
    { value, onChange, rows }: { value: string; onChange: (value: string) => void; rows: number },
) {
    const editorRef = useRef<HTMLDivElement>(null);

    const extractTextFromEditor = (): string => {
        if (!editorRef.current) {
            return '';
        }

        let text = '';
        const childNodes = Array.from(editorRef.current.childNodes);

        for (const node of childNodes) {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                if (element.classList.contains('placeholder-pill')) {
                    text += element.getAttribute('data-placeholder') || '';
                }
            }
        }

        return text;
    };

    const getCursorOffset = (): number => {
        if (!editorRef.current) {
            return 0;
        }

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return 0;
        }

        const range = selection.getRangeAt(0);
        let offset = 0;
        const treeWalker = document.createTreeWalker(
            editorRef.current,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            null
        );

        let node: Node | null;
        while ((node = treeWalker.nextNode())) {
            if (node === range.startContainer) {
                offset += range.startOffset;
                break;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                offset += node.textContent?.length || 0;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                if (element.classList.contains('placeholder-pill')) {
                    offset += element.getAttribute('data-placeholder')?.length || 0;
                }
            }
        }

        return offset;
    };

    const restoreCursorPosition = (offset: number) => {
        if (!editorRef.current) {
            return;
        }

        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        let currentOffset = 0;
        const treeWalker = document.createTreeWalker(
            editorRef.current,
            NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
            null
        );

        let node: Node | null;
        while ((node = treeWalker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE) {
                const length = node.textContent?.length || 0;
                if (currentOffset + length >= offset) {
                    const range = document.createRange();
                    range.setStart(node, Math.min(offset - currentOffset, length));
                    range.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(range);
                    return;
                }
                currentOffset += length;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                if (element.classList.contains('placeholder-pill')) {
                    const length = element.getAttribute('data-placeholder')?.length || 0;
                    if (currentOffset + length >= offset) {
                        // Position after the pill
                        const range = document.createRange();
                        range.setStartAfter(element);
                        range.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(range);
                        return;
                    }
                    currentOffset += length;
                }
            }
        }

        // If offset is beyond content, place at end
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
    };

    const renderContent = useCallback((text: string) => {
        if (!editorRef.current) {
            return;
        }

        // Save cursor position
        const selection = window.getSelection();
        let cursorOffset = 0;
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            if (editorRef.current.contains(range.startContainer)) {
                cursorOffset = getCursorOffset();
            }
        }

        // Clear and rebuild content
        editorRef.current.innerHTML = '';

        const parts = parseTextWithPlaceholders(text);
        if (parts.length === 0) {
            // Add empty text node to allow typing
            editorRef.current.appendChild(document.createTextNode(''));
        } else {
            for (const part of parts) {
                if (part.type === 'text') {
                    editorRef.current.appendChild(document.createTextNode(part.content));
                } else {
                    const pill = document.createElement('span');
                    pill.className =
                        'placeholder-pill inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-300 cursor-move font-mono text-sm mx-0.5';
                    pill.contentEditable = 'false';
                    pill.draggable = true;
                    pill.setAttribute('data-placeholder', part.content);
                    pill.textContent = part.content;
                    editorRef.current.appendChild(pill);
                }
            }
        }

        // Restore cursor position
        setTimeout(() => {
            restoreCursorPosition(cursorOffset);
        }, 0);
    }, []);

    // Initialize content
    useEffect(() => {
        if (!editorRef.current) {
            return;
        }

        // Only update if the content is different from what we're showing
        const currentText = extractTextFromEditor();
        if (currentText !== value) {
            renderContent(value);
        }
    }, [ value, renderContent ]);

    const handleInput = () => {
        const text = extractTextFromEditor();
        onChange(text);
    };

    const handleDragStart = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('placeholder-pill')) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', target.getAttribute('data-placeholder') || '');
            target.style.opacity = '0.4';
        }
    };

    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('placeholder-pill')) {
            target.style.opacity = '1';
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        if (text) {
            document.execCommand('insertText', false, text);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (!editorRef.current) {
            return;
        }

        const placeholder = e.dataTransfer.getData('text/plain');
        if (!placeholder) {
            return;
        }

        // Get the drop position
        const range = document.caretRangeFromPoint(e.clientX, e.clientY);
        if (!range || !editorRef.current.contains(range.startContainer)) {
            return;
        }

        // Remove the dragged pill from the editor
        const pills = editorRef.current.querySelectorAll('.placeholder-pill');
        pills.forEach(pill => {
            if (pill.getAttribute('data-placeholder') === placeholder) {
                pill.remove();
            }
        });

        // Insert at drop position
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);

            const pill = document.createElement('span');
            pill.className =
                'placeholder-pill inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded border border-blue-300 cursor-move font-mono text-sm mx-0.5';
            pill.contentEditable = 'false';
            pill.draggable = true;
            pill.setAttribute('data-placeholder', placeholder);
            pill.textContent = placeholder;

            range.insertNode(pill);

            // Move cursor after the pill
            range.setStartAfter(pill);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        const text = extractTextFromEditor();
        onChange(text);
    };

    return (
        <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            onPaste={handlePaste}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className='border border-gray-300 rounded p-2 min-h-[100px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        />
    );
}

export function EditModeProvider({ children, routeId }: { children: React.ReactNode; routeId: string }) {
    const [ searchParams ] = useSearchParams();
    const editModePassword = searchParams.get('editTranslations');
    const loaderData = useRouteLoaderData<{ isEditModeValid: boolean }>(routeId);
    const isEditMode = loaderData?.isEditModeValid ?? false;

    return <EditModeContext.Provider value={{ isEditMode, editModePassword }}>{children}</EditModeContext.Provider>;
}

export function EditableText({ ns, translationKey, t, opts, pluralVariants }: EditableTextProps) {
    const fetcher = useFetcher();
    const { i18n } = useTranslation();
    const { isEditMode, editModePassword } = useContext(EditModeContext);
    const [ activeEditLang, setActiveEditLang ] = useState<'en' | 'de'>('en');
    const [ hasPluralVariants, setHasPluralVariants ] = useState(false);
    const [ draft, setDraft ] = useState('');
    const [ draftZero, setDraftZero ] = useState(pluralVariants?.zero || '');
    const [ draftOne, setDraftOne ] = useState(pluralVariants?.one || '');
    const [ draftOther, setDraftOther ] = useState(pluralVariants?.other || '');
    const [ translationsEn, setTranslationsEn ] = useState({ base: '', zero: '', one: '', other: '' });
    const [ translationsDe, setTranslationsDe ] = useState({ base: '', zero: '', one: '', other: '' });
    const [ isHovered, setIsHovered ] = useState(false);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ timeoutId, setTimeoutId ] = useState<NodeJS.Timeout | null>(null);
    const [ buttonPosition, setButtonPosition ] = useState<{ x: number; y: number } | null>(null);
    const [ spanRef, setSpanRef ] = useState<HTMLSpanElement | null>(null);
    const [ contentRect, setContentRect ] = useState<DOMRect | null>(null);
    // Keep internal value in sync if pluralVariants changes from outside (but not while editing)
    useEffect(() => {
        if (isEditing) {
            return; // Don't reset values while the dialog is open
        }
        setDraft('');
        setDraftZero(pluralVariants?.zero || '');
        setDraftOne(pluralVariants?.one || '');
        setDraftOther(pluralVariants?.other || '');
    }, [ pluralVariants, isEditing ]);

    // Global mousemove listener using cached rect for hit test
    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        let rafId: number | null = null;

        const handleMouseMove = (e: MouseEvent) => {
            if (rafId) {
                return; // Skip if we're already processing a frame
            }

            rafId = requestAnimationFrame(() => {
                rafId = null;

                if (!spanRef) {
                    return;
                }
                const { clientX: x, clientY: y } = e;
                const range = document.createRange();
                range.selectNodeContents(spanRef);
                const rect = range.getBoundingClientRect();

                const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

                if (inside) {
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        setTimeoutId(null);
                    }

                    setIsHovered(true);
                    setButtonPosition({ x: rect.right + 2, y: rect.top + 12 });
                    setContentRect(rect);
                } else if (isHovered) {
                    // Mouse moved away from the span; hide after delay
                    if (!timeoutId) {
                        const newTimeoutId = setTimeout(() => setIsHovered(false), 1000);
                        setTimeoutId(newTimeoutId);
                    }
                }
            });
        };

        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [ isEditMode, isHovered, spanRef, timeoutId ]);

    const openEditor = async () => {
        if (!isEditMode) {
            return;
        }

        // Ensure both EN and DE languages are loaded
        await i18n.loadLanguages([ 'en', 'de' ]);

        // Load EN translations
        const enBundle = i18n.getResourceBundle('en', ns) || {};
        const enBase = dotAccess(enBundle, translationKey) as string || '';
        const enZero = dotAccess(enBundle, `${translationKey}_zero`) as string || '';
        const enOne = dotAccess(enBundle, `${translationKey}_one`) as string || '';
        const enOther = dotAccess(enBundle, `${translationKey}_other`) as string || '';
        console.log('Loaded EN translations for editor', { enBase, enZero, enOne, enOther, translationKey, enBundle });
        setTranslationsEn({ base: enBase, zero: enZero, one: enOne, other: enOther });

        // Load DE translations
        const deBundle = i18n.getResourceBundle('de', ns) || {};
        const deBase = dotAccess(deBundle, translationKey) as string || '';
        const deZero = dotAccess(deBundle, `${translationKey}_zero`) as string || '';
        const deOne = dotAccess(deBundle, `${translationKey}_one`) as string || '';
        const deOther = dotAccess(deBundle, `${translationKey}_other`) as string || '';
        setTranslationsDe({ base: deBase, zero: deZero, one: deOne, other: deOther });

        // Check if either language has plural variants
        const hasPlurals = !!(enZero || enOne || enOther || deZero || deOne || deOther);
        setHasPluralVariants(hasPlurals);

        // Set active language and load current translations
        const currentLang = i18n.language.startsWith('de') ? 'de' : 'en';
        setActiveEditLang(currentLang);

        if (currentLang === 'en') {
            setDraft(enBase);
            setDraftZero(enZero);
            setDraftOne(enOne);
            setDraftOther(enOther);
        } else {
            setDraft(deBase);
            setDraftZero(deZero);
            setDraftOne(deOne);
            setDraftOther(deOther);
        }

        setIsEditing(true);
    };

    const closeEditor = () => {
        setIsEditing(false);
    };

    // Handle Escape key to close editor
    useEffect(() => {
        if (!isEditing) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeEditor();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [ isEditing ]);

    // Handle language switching in dialog
    const switchEditLanguage = (lang: 'en' | 'de') => {
        // Save current drafts to the previous language
        if (activeEditLang === 'en') {
            setTranslationsEn({ base: draft, zero: draftZero, one: draftOne, other: draftOther });
        } else {
            setTranslationsDe({ base: draft, zero: draftZero, one: draftOne, other: draftOther });
        }

        // Load drafts for new language
        if (lang === 'en') {
            setDraft(translationsEn.base);
            setDraftZero(translationsEn.zero);
            setDraftOne(translationsEn.one);
            setDraftOther(translationsEn.other);
        } else {
            setDraft(translationsDe.base);
            setDraftZero(translationsDe.zero);
            setDraftOne(translationsDe.one);
            setDraftOther(translationsDe.other);
        }

        setActiveEditLang(lang);

        // Change the website language
        void i18n.changeLanguage(lang);
    };

    const handleSave = async () => {
        setIsEditing(false);
        setIsHovered(false);
        if (timeoutId) {
            clearTimeout(timeoutId);
            setTimeoutId(null);
        }

        // Save current drafts to the active language
        if (activeEditLang === 'en') {
            setTranslationsEn({ base: draft, zero: draftZero, one: draftOne, other: draftOther });
        } else {
            setTranslationsDe({ base: draft, zero: draftZero, one: draftOne, other: draftOther });
        }

        // Update both EN and DE resource bundles optimistically
        const updateBundle = (lang: 'en' | 'de', translations: typeof translationsEn) => {
            const resourceBundle = i18n.getResourceBundle(lang, ns) || {};
            const keys = translationKey.split('.');
            let current: any = resourceBundle;
            for (let i = 0; i < keys.length - 1; i++) {
                const key = keys[i];
                if (!key) {
                    continue;
                }
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key];
            }
            const lastKey = keys[keys.length - 1];
            if (lastKey) {
                if (translations.base || !hasPluralVariants) {
                    current[lastKey] = translations.base;
                }
                if (hasPluralVariants) {
                    if (translations.zero) {
                        current[`${lastKey}_zero`] = translations.zero;
                    }
                    if (translations.one) {
                        current[`${lastKey}_one`] = translations.one;
                    }
                    if (translations.other) {
                        current[`${lastKey}_other`] = translations.other;
                    }
                }
            }
            i18n.addResourceBundle(lang, ns, resourceBundle, true, true);
        };

        const finalEn = activeEditLang === 'en'
            ? { base: draft, zero: draftZero, one: draftOne, other: draftOther }
            : translationsEn;
        const finalDe = activeEditLang === 'de'
            ? { base: draft, zero: draftZero, one: draftOne, other: draftOther }
            : translationsDe;

        updateBundle('en', finalEn);
        updateBundle('de', finalDe);

        // Persist via Remix action - submit both EN and DE
        const submissionData: Record<string, string> = {
            ns,
            key: translationKey,
            editModePassword: editModePassword || ''
        };

        // Add EN translations
        if (finalEn.base || !hasPluralVariants) {
            submissionData['en_value'] = finalEn.base;
        }
        if (hasPluralVariants) {
            if (finalEn.zero) {
                submissionData['en_value_zero'] = finalEn.zero;
            }
            if (finalEn.one) {
                submissionData['en_value_one'] = finalEn.one;
            }
            if (finalEn.other) {
                submissionData['en_value_other'] = finalEn.other;
            }
        }

        // Add DE translations
        if (finalDe.base || !hasPluralVariants) {
            submissionData['de_value'] = finalDe.base;
        }
        if (hasPluralVariants) {
            if (finalDe.zero) {
                submissionData['de_value_zero'] = finalDe.zero;
            }
            if (finalDe.one) {
                submissionData['de_value_one'] = finalDe.one;
            }
            if (finalDe.other) {
                submissionData['de_value_other'] = finalDe.other;
            }
        }

        fetcher.submit(
            submissionData,
            {
                method: 'post',
                action: '/admin/translations' // adjust to your route
            }
        );
    };

    // In non-edit mode: behave as layout-neutral as possible
    if (!isEditMode) {
        return <span className='contents'>{t(`${ns}:${translationKey}`, opts)}</span>;
    }

    return (
        <>
            <span ref={setSpanRef} className='contents'>{t(`${ns}:${translationKey}`, opts)}</span>

            {/* Red rectangle outline */}
            {isHovered && contentRect && typeof document !== 'undefined' && createPortal(
                <div
                    className='fixed border border-red-500 bg-transparent pointer-events-none'
                    style={{
                        top: `${contentRect.top}px`,
                        left: `${contentRect.left}px`,
                        width: `${contentRect.width}px`,
                        height: `${contentRect.height}px`,
                        zIndex: 999999998
                    }}
                />,
                document.body
            )}

            {/* Edit button portal */}
            {isHovered && buttonPosition && typeof document !== 'undefined' && createPortal(
                <button
                    type='button'
                    aria-label='Edit text'
                    onClick={(e) => {
                        e.stopPropagation();
                        void openEditor();
                    }}
                    onMouseEnter={() => {
                        if (timeoutId) {
                            clearTimeout(timeoutId);
                            setTimeoutId(null);
                        }
                    }}
                    className='fixed -translate-y-1/2 text-xs border border-gray-300 bg-white/95 cursor-pointer px-1 py-0.5 rounded shadow-md'
                    style={{
                        top: `${buttonPosition.y}px`,
                        left: `${buttonPosition.x + 10}px`,
                        zIndex: 999999999
                    }}
                >
                    ✏️
                </button>,
                document.body
            )}

            {/* Fullscreen overlay editor portal */}
            {isEditing && typeof document !== 'undefined' && createPortal(
                <div
                    onClick={closeEditor}
                    className='fixed inset-0 bg-black/40 flex items-center justify-center'
                    style={{ zIndex: 9999999999 }}
                >
                    <div onClick={(e) => e.stopPropagation()} className='bg-white p-6 rounded-lg w-120 shadow-2xl'>
                        <div className='flex items-center justify-between mb-3'>
                            <h2 className='m-0 text-lg'>Edit text</h2>
                            <div className='flex gap-1 border border-gray-300 rounded overflow-hidden'>
                                <button
                                    type='button'
                                    onClick={() => switchEditLanguage('en')}
                                    className={`px-3 py-1 text-sm font-medium transition-colors ${
                                        activeEditLang === 'en'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-white text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    EN
                                </button>
                                <button
                                    type='button'
                                    onClick={() => switchEditLanguage('de')}
                                    className={`px-3 py-1 text-sm font-medium transition-colors ${
                                        activeEditLang === 'de'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-white text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    DE
                                </button>
                            </div>
                        </div>

                        <p className='text-xs text-gray-600 mt-0 mb-2'>
                            {activeEditLang.toUpperCase()} / {ns} / {translationKey}
                        </p>

                        {!hasPluralVariants && (
                            <button
                                type='button'
                                onClick={() => setHasPluralVariants(true)}
                                className='mb-2 text-xs text-blue-600 hover:text-blue-800 underline'
                            >
                                + Add plural variants
                            </button>
                        )}

                        {!hasPluralVariants
                            ? <InteractiveTextEditor value={draft} onChange={setDraft} rows={6} />
                            : (
                                <div className='flex flex-col gap-3'>
                                    <div>
                                        <label className='text-sm font-medium block mb-1'>Default (optional)</label>
                                        <InteractiveTextEditor value={draft} onChange={setDraft} rows={3} />
                                    </div>
                                    <div>
                                        <label className='text-sm font-medium block mb-1'>Zero (count=0)</label>
                                        <InteractiveTextEditor value={draftZero} onChange={setDraftZero} rows={3} />
                                    </div>
                                    <div>
                                        <label className='text-sm font-medium block mb-1'>One (count=1)</label>
                                        <InteractiveTextEditor value={draftOne} onChange={setDraftOne} rows={3} />
                                    </div>
                                    <div>
                                        <label className='text-sm font-medium block mb-1'>Other (count≠0,1)</label>
                                        <InteractiveTextEditor value={draftOther} onChange={setDraftOther} rows={3} />
                                    </div>
                                </div>
                            )}

                        <div className='mt-4 flex justify-end gap-2'>
                            <Button type='button' onClick={closeEditor} className='rounded'>Cancel</Button>
                            <Button type='button' onClick={handleSave} className='rounded'>Save</Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export function useEditableTranslation(
    ns?: Parameters<typeof useTranslation>[0],
    options?: Parameters<typeof useTranslation>[1],
) {
    const { t, i18n } = useTranslation(ns, options);

    return {
        rawT: t,
        t: (translationKey: string, opts?: Parameters<typeof t>[1]) => {
            let namespace = ns;
            const [ firstPart, secondPart ] = translationKey.split(':');
            if (secondPart?.length) {
                translationKey = secondPart;
                namespace = firstPart;
            }
            const resourceBundle = i18n.getResourceBundle(
                i18n.language,
                typeof namespace === 'string' ? namespace : (i18n.options.defaultNS as string) || 'translation'
            );

            // Check for plural variants
            const pluralVariants = {
                zero: dotAccess(resourceBundle, `${translationKey}_zero`) as string | undefined,
                one: dotAccess(resourceBundle, `${translationKey}_one`) as string | undefined,
                other: dotAccess(resourceBundle, `${translationKey}_other`) as string | undefined
            };

            return (
                <EditableText
                    t={t}
                    opts={opts}
                    ns={typeof namespace === 'string' ? namespace : (i18n.options.defaultNS as string) || 'translation'}
                    translationKey={translationKey}
                    pluralVariants={pluralVariants}
                />
            );
        }
    };
}

function dotAccess(obj: any, path: string) {
    return path.split('.').reduce((o, p) => (o ? o[p] : undefined), obj);
}

export type TranslateFunc = ReturnType<typeof useEditableTranslation>['t'];
export type TranslateRawFunc = ReturnType<typeof useEditableTranslation>['rawT'];
